import { useQuery } from '@tanstack/react-query';
import { parseHttpError } from '@upme/api-client';
import type { AdminUserDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { listCitiesOptions } from '../../cities/queries';
import { roleErrorText } from '../lib/role-error-text';
import { useToggleAdminRole } from '../mutations';
import { CityMultiSelect } from './city-multi-select';

interface Props {
  user: AdminUserDto;
}

/** «Менеджер города» = CITY_MODERATOR с городами роли (#166). Выдача и правка — одним PATCH. */
export function CityModeratorSection({ user }: Props) {
  const cities = useQuery(listCitiesOptions());
  const toggle = useToggleAdminRole(user.id);
  const isModerator = user.adminRoles.includes('CITY_MODERATOR');
  const [draft, setDraft] = useState<string[]>(user.moderatedCityIds);

  // Ответ PATCH обновляет user в кэше — черновик синхронизируем с сервером.
  // Зависим от значения, а не от ссылки: чужие мутации карточки не должны стирать несохранённый выбор.
  const savedKey = user.moderatedCityIds.join();
  useEffect(() => setDraft(savedKey ? savedKey.split(',') : []), [savedKey]);

  const ordered = (ids: string[]) => (cities.data ?? []).map((c) => c.id).filter((id) => ids.includes(id));
  const changed = ordered(draft).join() !== ordered(user.moderatedCityIds).join();
  const busy = toggle.isPending || user.isFake;

  async function submit(body: Parameters<typeof toggle.mutateAsync>[0], ok: string) {
    try {
      await toggle.mutateAsync(body);
      toast.success(ok);
    } catch (err) {
      const api = await parseHttpError(err);
      toast.error(roleErrorText(api?.message, 'Не удалось изменить роль'));
    }
  }

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="flex items-center gap-3">
        <span className="font-medium text-sm">Менеджер города</span>
        {user.isFake && <span className="text-xs text-muted-foreground">фейку роль не выдаётся</span>}
      </div>
      <CityMultiSelect cities={cities.data ?? []} value={draft} onChange={setDraft} disabled={busy} />
      <div className="flex gap-2">
        {isModerator ? (
          <>
            <Button
              type="button"
              size="sm"
              disabled={busy || draft.length === 0 || !changed}
              onClick={() =>
                void submit({ role: 'CITY_MODERATOR', value: true, cityIds: draft }, 'Города сохранены')
              }
            >
              Сохранить города
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void submit({ role: 'CITY_MODERATOR', value: false }, 'Роль снята')}
            >
              Снять роль
            </Button>
          </>
        ) : (
          <Button
            type="button"
            size="sm"
            disabled={busy || draft.length === 0}
            onClick={() =>
              void submit({ role: 'CITY_MODERATOR', value: true, cityIds: draft }, 'Роль выдана')
            }
          >
            Выдать роль
          </Button>
        )}
      </div>
    </div>
  );
}
